/**
 * Drafting commands (P6f — docs/bim-plan/08 Part 8): user-authored dimensions and
 * text, stored as view-scoped records in the `drafting` top map — never model
 * elements, never in the geometry worker. The governing rule: DRAFTING IS STORED BUT
 * ITS VALUES DERIVE (principle 4). A dimension stores WHICH points it measures —
 * element+node references when the placement click snapped to real geometry — and
 * `resolveWitness` re-reads the CURRENT node position on every render, so a dimension
 * updates live with the model and can never show a stale number. A witness whose
 * element/node has been deleted degrades gracefully to its last static point.
 */
import type {
  AnnoTypeId, DetailComponent, DraftingId, DraftingRecord, DraftingWitness, DraftLayerId,
  ElementId, GridId, LevelId, NodeId, Path2D, Pt2, ViewId,
  WallReferenceLine,
} from '../types'
import { newDraftingId, newNodeId } from '../ids'
import { getDrafting, getView, listDrafting, listGrids, maps, recordToYMap, resolveElement, transact, type ModelDoc } from '../doc'
import { bulgeToArc, flattenPathPts, type EdgeArc } from '../arc'
import { centerlineOffsetFt, referenceOffsets, wallLayerFaceOffsets } from '../locationLine'
import { objectFootprintCorners, columnProfileRing, hostedFootprintCorners } from '../snap'
import { isPointFixtureKind } from '../categories/fixture/fixtureKinds'
import { openingAlignLines } from '../openingAlign'

export type DraftingScope = DraftingRecord['scope']

/**
 * VIEW SCOPING round: the surface a drafting interaction happens ON — the live plan
 * of a level, or one stored view (a typed/saved plan view, a section, an elevation).
 * Same shape as DraftingScope minus the `live` stamp; every filter/stamp helper below
 * takes one of these so the UI, sheets, and PDF all resolve scope identically.
 */
export type DraftingSurface = { kind: 'level'; levelId: LevelId } | { kind: 'view'; viewId: ViewId }

export function createDimension(
  model: ModelDoc,
  input: {
    scope: DraftingScope
    a?: DraftingWitness
    b?: DraftingWitness
    /** r22 string form: all measured points in order (≥2); preferred over a/b */
    witnesses?: DraftingWitness[]
    offsetFt: number
    /** r23: the annotation type carrying text size / accuracy / witness gap */
    typeId?: AnnoTypeId | null
    /** CURVES round: radius / arc-length dimensions (see DraftingRecord.dimType) */
    dimType?: 'radius' | 'arclen'
    edgeRef?: { elementId: ElementId; nodeId: NodeId } | null
    /** radius only: the label anchor point */
    pt?: Pt2
    /** DIMENSIONS SUITE: cartesian string lock — see DraftingRecord.axis */
    axis?: 'x' | 'y'
  },
): DraftingId {
  const id = newDraftingId()
  const witnesses = input.witnesses && input.witnesses.length >= 2 ? input.witnesses : null
  transact(model, () => {
    maps(model).drafting.set(id, recordToYMap({
      id, scope: input.scope, kind: 'dimension',
      ...(witnesses ? { witnesses } : input.a && input.b ? { a: input.a, b: input.b } : {}),
      offsetFt: input.offsetFt,
      ...(input.typeId ? { typeId: input.typeId } : {}),
      ...(input.dimType ? { dimType: input.dimType } : {}),
      ...(input.edgeRef ? { edgeRef: input.edgeRef } : {}),
      ...(input.pt ? { pt: input.pt } : {}),
      ...(input.axis ? { axis: input.axis } : {}),
    }))
  })
  return id
}

// ---------- DIMENSIONS SUITE round: cartesian string snap ----------

/** How close (degrees) an offset drag must pull toward an axis to snap the string. */
export const CARTESIAN_SNAP_DEG = 15

/**
 * The Revit offset-drag behavior: dragging a two-point dimension's line toward the
 * view's vertical snaps the STRING to horizontal (it reads the pair's Δx), toward
 * the horizontal snaps it to vertical (Δy); anywhere else stays aligned (null).
 * Never snaps when the pair is already axis-parallel (aligned IS cartesian there)
 * or when the snapped projection would be degenerate.
 */
export function cartesianStringAxis(a: Pt2, b: Pt2, at: Pt2, tolDeg = CARTESIAN_SNAP_DEG): 'x' | 'y' | null {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  if (Math.hypot(dx, dy) < 1e-6) return null
  if (Math.abs(dx) < 1e-6 || Math.abs(dy) < 1e-6) return null   // pair already axis-parallel
  const ox = at[0] - (a[0] + b[0]) / 2, oy = at[1] - (a[1] + b[1]) / 2
  const olen = Math.hypot(ox, oy)
  if (olen < 1e-6) return null
  const tol = Math.sin((tolDeg * Math.PI) / 180)
  // pull near the VERTICAL axis → horizontal string; near the HORIZONTAL → vertical
  if (Math.abs(ox) / olen <= tol) return 'x'
  if (Math.abs(oy) / olen <= tol) return 'y'
  return null
}

/**
 * The direction a dimension's string runs, when the record stores/derives one —
 * null = the default pick-order derivation (the renderer's own fallback). Two
 * sources: the cartesian axis lock, and (ALIGNED) the first lineRef witness's LIVE
 * reference-line normal — so an aligned string stays perpendicular to its first
 * picked wall even as that wall moves or rotates (values derive).
 */
export function dimensionDirection(model: ModelDoc, rec: DraftingRecord): Pt2 | null {
  if (rec.kind !== 'dimension' || rec.dimType) return null
  if (rec.axis === 'x') return [1, 0]
  if (rec.axis === 'y') return [0, 1]
  // the LEAD line-anchored witness steers (a dead lead degrades to pick order — the
  // witness-degradation convention); an opening's line steers like a wall's
  const lead = dimensionWitnessList(rec).find((w) => w.lineRef || w.openingRef || w.gridRef || w.footRef)
  if (lead) {
    const line = witnessRefLine(model, lead)
    if (line) return [-line.u[1], line.u[0]]
  }
  return null
}

/** DRAFTING PARITY: the drafting record id behind a `draft:<id>` edge reference, else null */
export function draftEdgeRecordId(elementId: string | null | undefined): string | null {
  return typeof elementId === 'string' && elementId.startsWith('draft:') ? elementId.slice(6) : null
}

/**
 * CURVES round: the LIVE arc a radius/arc-length dimension measures — re-resolved
 * against the current element path on every render (values derive), null when the
 * element/edge/bulge is gone (the caller degrades to the stored witness points).
 */
export function dimensionEdgeArc(model: ModelDoc, rec: DraftingRecord): EdgeArc | null {
  if (!rec.edgeRef) return null
  // DRAFTING PARITY (2026-10-12): a radius / arc-length dimension on a DRAFTED arc
  // (a line or hatch region's curved edge) — `draft:<recordId>`, the drafting-ink
  // snap id convention — reads that record's own path, live
  const did = draftEdgeRecordId(rec.edgeRef.elementId)
  const path = did
    ? (getDrafting(model, did as DraftingId)?.path ?? null)
    : (() => {
      const el = resolveElement(model, rec.edgeRef!.elementId)
      return (el?.params.path ?? el?.params.boundary) as Path2D | null | undefined
    })()
  if (!path?.nodes) return null
  const i = path.nodes.findIndex((n) => n.id === rec.edgeRef!.nodeId)
  if (i < 0) return null
  const j = (i + 1) % path.nodes.length
  if (!path.closed && i === path.nodes.length - 1) return null
  return bulgeToArc(path.nodes[i].pt, path.nodes[j].pt, path.nodes[i].bulge)
}

export function createText(
  model: ModelDoc,
  input: {
    scope: DraftingScope; pt: Pt2; text: string
    leader?: Pt2 | null
    leaders?: Pt2[] | null
    bend?: Pt2 | null
    widthIn?: number | null
    /** r23: the annotation type carrying the text size */
    typeId?: AnnoTypeId | null
  },
): DraftingId {
  const id = newDraftingId()
  transact(model, () => {
    maps(model).drafting.set(id, recordToYMap({
      id, scope: input.scope, kind: 'text',
      pt: input.pt, text: input.text,
      ...(input.leaders && input.leaders.length ? { leaders: input.leaders }
        : input.leader ? { leader: input.leader } : {}),
      ...(input.bend ? { bend: input.bend } : {}),
      ...(input.widthIn ? { widthIn: input.widthIn } : {}),
      ...(input.typeId ? { typeId: input.typeId } : {}),
    }))
  })
  return id
}

/**
 * SPOT ELEVATION (levels-as-datums round): stores the plan point it reads, optionally
 * the element whose surface it reads there, and the last resolved z as a fallback. The
 * displayed number always derives from the geometry — see DraftingRecord.
 */
export function createSpot(
  model: ModelDoc,
  input: {
    scope: DraftingScope; pt: Pt2
    elementRef?: { elementId: ElementId } | null
    zFt?: number
  },
): DraftingId {
  const id = newDraftingId()
  transact(model, () => {
    maps(model).drafting.set(id, recordToYMap({
      id, scope: input.scope, kind: 'spot', pt: input.pt,
      ...(input.elementRef ? { elementRef: input.elementRef } : {}),
      ...(input.zFt != null ? { zFt: input.zFt } : {}),
    }))
  })
  return id
}

// ---------- GL5a: drafting LINEWORK + HATCH REGIONS (the detail toolset) ----------

/** Build a drafting Path2D from bare points (+ optional per-edge bulges), minting node ids. */
export function draftPathFrom(pts: Pt2[], closed: boolean, bulges?: (number | undefined)[]): Path2D {
  return {
    nodes: pts.map((pt, i) => ({
      id: newNodeId(),
      pt: [pt[0], pt[1]] as Pt2,
      ...(bulges?.[i] ? { bulge: bulges[i] } : {}),
    })),
    closed,
  }
}

/**
 * GL5a: drafting LINEWORK — a polyline/polygon of pure 2D ink (per-edge bulges carry
 * arcs). `component` is the pen (DETAIL_COMPONENTS; absent = 'medium').
 */
export function createDraftLine(
  model: ModelDoc,
  input: { scope: DraftingScope; path: Path2D; component?: DetailComponent; layerId?: DraftLayerId },
): DraftingId {
  const id = newDraftingId()
  transact(model, () => {
    maps(model).drafting.set(id, recordToYMap({
      id, scope: input.scope, kind: 'line', path: input.path,
      // CAD round: with a layer the pen is ByLayer — no component stored at all
      ...(input.layerId ? { layerId: input.layerId }
        : input.component && input.component !== 'medium' ? { component: input.component } : {}),
      ...(input.layerId && input.component ? { component: input.component } : {}),
    }))
  })
  return id
}

/**
 * CAD r11 (Matt: "we need polyline and non polyline options"): the SEGMENTS
 * counterpart to createDraftLine — the SAME clicked point chain becomes N
 * independent 2-node line records (one per consecutive pair) instead of one
 * multi-node polyline. Useful when segments need to be edited/erased/offset
 * independently later (EXPLODE remains the escape hatch for a polyline already
 * drawn as one). One transact = one undo step for the whole chain, matching
 * every other multi-record create in this codebase (e.g. arrayDrafting).
 */
export function createDraftLineSegments(
  model: ModelDoc,
  input: { scope: DraftingScope; pts: Pt2[]; closed: boolean; component?: DetailComponent; layerId?: DraftLayerId },
): DraftingId[] {
  const segPts = input.closed ? [...input.pts, input.pts[0]] : input.pts
  const ids: DraftingId[] = []
  transact(model, () => {
    const { drafting } = maps(model)
    for (let i = 0; i < segPts.length - 1; i++) {
      const id = newDraftingId()
      const path: Path2D = {
        closed: false,
        nodes: [
          { id: newNodeId(), pt: segPts[i] },
          { id: newNodeId(), pt: segPts[i + 1] },
        ],
      }
      drafting.set(id, recordToYMap({
        id, scope: input.scope, kind: 'line', path,
        ...(input.layerId ? { layerId: input.layerId }
          : input.component && input.component !== 'medium' ? { component: input.component } : {}),
        ...(input.layerId && input.component ? { component: input.component } : {}),
      }))
      ids.push(id)
    }
  })
  return ids
}

/**
 * GL5a: a HATCH REGION — a CLOSED drafting path filled with a CUT_PATTERNS token
 * through the pattern-fill machinery. The path is stored closed unconditionally.
 */
export function createDraftRegion(
  model: ModelDoc,
  input: { scope: DraftingScope; path: Path2D; pattern: string; layerId?: DraftLayerId },
): DraftingId {
  const id = newDraftingId()
  transact(model, () => {
    maps(model).drafting.set(id, recordToYMap({
      id, scope: input.scope, kind: 'region',
      path: { ...input.path, closed: true },
      pattern: input.pattern,
      ...(input.layerId ? { layerId: input.layerId } : {}),
    }))
  })
  return id
}

/**
 * A line/region record's path flattened to polyline points (arcs tessellated through
 * the ONE shared arc.ts derivation) — the renderer/hit-test/snap/bbox feed. A closed
 * path's flatten ends back at its first point (flattenPathPts' closed-wrap segment),
 * so consumers can treat the result as a plain polyline in both cases.
 */
export function draftPathPts(rec: DraftingRecord, tolFt?: number): Pt2[] {
  if (!rec.path?.nodes?.length) return []
  return flattenPathPts(rec.path, tolFt)
}

/**
 * GL5b: drafting owned by a FIRM-library detail (the view's scope.firmId is set) is
 * read-only in the drawing flow — updateDrafting/deleteDrafting refuse it, so no UI
 * path can mutate a firm standard's content. The firm-library sync itself writes via
 * maps() directly and is unaffected; "Duplicate to customize" is the editing path.
 */
export function firmDetailLocked(model: ModelDoc, scope: DraftingScope | undefined): boolean {
  if (!scope || scope.kind !== 'view') return false
  const v = getView(model, scope.viewId)
  return !!v && v.scope.kind === 'detail' && !!v.scope.firmId
}

/** Shallow field update — pass `null` to clear a field (e.g. leader: null). */
export function updateDrafting(
  model: ModelDoc,
  id: DraftingId,
  patch: Partial<Pick<DraftingRecord,
    'a' | 'b' | 'witnesses' | 'offsetFt' | 'textOffsets' | 'pt' | 'text' | 'leader' | 'leaders' | 'bend' | 'widthIn'
    | 'elementRef' | 'zFt' | 'typeId' | 'edgeRef' | 'path' | 'component' | 'pattern' | 'layerId' | 'fillColor'
    | 'elbow' | 'rotDeg' | 'bold' | 'italic'>>
    // null clears (the shallow-patch delete contract) — axis/elevMode have no stored null form
    & { axis?: 'x' | 'y' | null; elevMode?: 'primary' | 'secondary' | 'both' | null; eq?: boolean | null },
): boolean {
  const em = maps(model).drafting.get(id)
  if (!em) return false
  if (firmDetailLocked(model, em.get('scope') as DraftingScope | undefined)) return false
  transact(model, () => {
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) em.delete(k)
      else em.set(k, v)
    }
  })
  return true
}

export function deleteDrafting(model: ModelDoc, id: DraftingId): boolean {
  const { drafting } = maps(model)
  const em = drafting.get(id)
  if (!em) return false
  if (firmDetailLocked(model, em.get('scope') as DraftingScope | undefined)) return false
  transact(model, () => { drafting.delete(id) })
  return true
}

/**
 * EXACT-scope filter (pre-scoping form): every level-scoped record of a level, or
 * every view-scoped record of one view. Renderers should use `draftingForSurface`
 * instead — it adds the live-plan isolation + grandfather rules; this stays for the
 * callers that genuinely want the raw scope match (and for old tests).
 */
export function draftingFor(
  model: ModelDoc,
  scope: { kind: 'level'; levelId: LevelId } | { kind: 'view'; viewId: ViewId },
): DraftingRecord[] {
  return listDrafting(model).filter((r) =>
    scope.kind === 'level'
      ? r.scope.kind === 'level' && r.scope.levelId === scope.levelId
      : r.scope.kind === 'view' && r.scope.viewId === scope.viewId)
}

// ---------- VIEW SCOPING round (docs/MODELER.md "PLAN ANNOTATION VIEW SCOPING") ----------

/**
 * A GRANDFATHERED record: level-scoped from before plan drafting was view-scoped,
 * so it can't know which plan view it meant. It keeps rendering exactly where it
 * always did (the live plan AND every plan view of its level) until someone adopts
 * it into a view — retirement is per record, never by decree.
 */
export function isGrandfathered(rec: DraftingRecord): boolean {
  return rec.scope.kind === 'level' && !rec.scope.live
}

/**
 * The scope a NEW record drawn on a surface stamps. The live plan (a bare level
 * surface, or the level's implicit UNTYPED plan view) stamps `level + live` — its
 * records are the live plan's own and never appear in typed/saved views. Any other
 * view (typed plan, section, elevation) stamps `view`. ONE derivation shared by
 * every drawing tool so a record's home can never depend on which tool placed it.
 */
export function scopeForSurface(model: ModelDoc, surface: DraftingSurface): DraftingScope {
  if (surface.kind === 'level') return { kind: 'level', levelId: surface.levelId, live: true }
  const v = getView(model, surface.viewId)
  // the untyped plan view IS the live plan (09 §6) — same scope as drawing on it.
  // (levelId presence is the real gate: level-free plans — site/roof, bim-plan/14 —
  // are always TYPED, so an untyped plan always has a level; the check keeps TS and
  // any malformed record honest.)
  if (v && v.scope.kind === 'plan' && !v.scope.planType && v.scope.levelId) {
    return { kind: 'level', levelId: v.scope.levelId, live: true }
  }
  return { kind: 'view', viewId: surface.viewId }
}

/**
 * The records a surface RENDERS (and hit-tests — one predicate for both):
 * - live plan (level surface / untyped plan view): every level-scoped record of the
 *   level — its own live-stamped ones plus every grandfathered one.
 * - typed/saved plan view: its own view-scoped records plus the level's
 *   grandfathered records (which keep rendering where they always did).
 * - section/elevation: its own view-scoped records (unchanged — always view-scoped).
 * Type-recipe KIND filtering (viewTypes.visibleDraftingKinds) composes on top.
 */
function draftingForSurfaceUnordered(model: ModelDoc, surface: DraftingSurface): DraftingRecord[] {
  if (surface.kind === 'level') {
    return listDrafting(model).filter((r) => r.scope.kind === 'level' && r.scope.levelId === surface.levelId)
  }
  const v = getView(model, surface.viewId)
  if (v && v.scope.kind === 'plan') {
    const levelId = v.scope.levelId
    if (!v.scope.planType) {
      // the live plan's own viewport identity: level records + anything stamped to it
      return listDrafting(model).filter((r) =>
        (r.scope.kind === 'level' && r.scope.levelId === levelId) ||
        (r.scope.kind === 'view' && r.scope.viewId === surface.viewId))
    }
    // STRUCTURAL PLANS (2026-10-21): the level's grandfathered records are the architect's
    // notes — a Framing / Foundation Plan shows only what was drawn on it
    const structural = v.scope.planType === 'framing' || v.scope.planType === 'foundation'
    return listDrafting(model).filter((r) =>
      (r.scope.kind === 'view' && r.scope.viewId === surface.viewId) ||
      (!structural && r.scope.kind === 'level' && r.scope.levelId === levelId && !r.scope.live))
  }
  return listDrafting(model).filter((r) => r.scope.kind === 'view' && r.scope.viewId === surface.viewId)
}

export function draftingForSurface(model: ModelDoc, surface: DraftingSurface): DraftingRecord[] {
  const recs = draftingForSurfaceUnordered(model, surface)
  // DRAW ORDER round: an untouched scope (every record's zOrder absent) returns in
  // its ORIGINAL order unchanged — byte-identical to every pre-round consumer
  // (rendering paints array order = painter's algorithm; hitDraftingAll walks it
  // backwards, "last in array" = topmost = hit first). Once a reorder command has
  // touched ANY record here, draftingOps.ts's backfill stamps EVERY sibling in the
  // same transaction — so a scope is either all-absent or all-explicit, never a mix.
  if (recs.every((r) => r.zOrder == null)) return recs
  // DRAW ORDER fix (2026-10-13): a record drawn AFTER a reorder has no zOrder yet — it
  // sits on TOP in the order it was made (Revit: a new detail lands in front), never
  // at 0 near the bottom where it used to fall
  const key = new Map(recs.map((r, i) => [r.id, r.zOrder ?? 1e12 + i]))
  return [...recs].sort((a, b) => key.get(a.id)! - key.get(b.id)!)
}

/**
 * Adopt one grandfathered record into a typed/saved plan view of its own level —
 * re-stamps the scope to `{kind:'view'}` and thereby retires its grandfather
 * rendering everywhere else. Refuses anything that isn't a grandfathered record,
 * any non-plan/untyped target, and any cross-level target (an adoption can only
 * ever narrow where a record shows, never teleport it to another story).
 */
export function adoptDraftingIntoView(model: ModelDoc, id: DraftingId, viewId: ViewId): boolean {
  const rec = getDrafting(model, id)
  if (!rec || !isGrandfathered(rec) || rec.scope.kind !== 'level') return false
  const v = getView(model, viewId)
  if (!v || v.scope.kind !== 'plan' || !v.scope.planType) return false
  if (v.scope.levelId !== rec.scope.levelId) return false
  const em = maps(model).drafting.get(id)
  if (!em) return false
  transact(model, () => { em.set('scope', { kind: 'view', viewId }) })
  return true
}

/** Bulk adopt: every grandfathered record on the view's level → this view. Returns the count. */
export function adoptLevelDraftingIntoView(model: ModelDoc, viewId: ViewId): number {
  const v = getView(model, viewId)
  if (!v || v.scope.kind !== 'plan' || !v.scope.planType) return 0
  let n = 0
  for (const rec of listDrafting(model)) {
    if (isGrandfathered(rec) && rec.scope.kind === 'level' && rec.scope.levelId === v.scope.levelId) {
      if (adoptDraftingIntoView(model, rec.id, viewId)) n++
    }
  }
  return n
}

/**
 * deleteView cleanup: a view's own drafting records die with it (they render nowhere
 * else and would otherwise leak forever — sections have quietly leaked theirs since
 * P6f). Grandfathered/level records are untouched by definition.
 */
export function deleteDraftingForView(model: ModelDoc, viewId: ViewId): number {
  const doomed = listDrafting(model).filter((r) => r.scope.kind === 'view' && r.scope.viewId === viewId)
  if (!doomed.length) return 0
  transact(model, () => { for (const r of doomed) maps(model).drafting.delete(r.id) })
  return doomed.length
}

/**
 * DIMENSIONS SUITE (ALIGNED): the CURRENT line a lineRef witness measures to — the
 * element edge named by its from-node, translated to a wall reference face when
 * `face` is set. Returned as a point + unit direction (an infinite line: only the
 * line matters to a projection, so miter effects at the edge's ENDS are irrelevant).
 * Null when the element/edge is gone or the edge is curved (the pick flow never
 * captures those; a later re-bow degrades the witness to its static point).
 */
export function witnessLine(
  model: ModelDoc,
  lr: NonNullable<DraftingWitness['lineRef']>,
): { p: Pt2; u: Pt2 } | null {
  const el = resolveElement(model, lr.elementId)
  const path = (el?.params.path ?? el?.params.boundary) as Path2D | null | undefined
  if (!el || !path?.nodes) return null
  const i = path.nodes.findIndex((n) => n.id === lr.nodeId)
  if (i < 0) return null
  const j = (i + 1) % path.nodes.length
  if (!path.closed && i === path.nodes.length - 1) return null
  if (path.nodes[i].bulge) return null   // curved spans excluded (CURVES-round scope)
  const a = path.nodes[i].pt, b = path.nodes[j].pt
  const len = Math.hypot(b[0] - a[0], b[1] - a[1])
  if (len < 1e-9) return null
  const u: Pt2 = [(b[0] - a[0]) / len, (b[1] - a[1]) / len]
  let off = 0
  if (typeof lr.layer === 'number' && el.category === 'wall' && el.assembly && el.assemblyThicknessFt > 0) {
    // ALIGNED ANY-LINE round: any assembly layer line (centerline-relative offsets
    // along the path's left normal, the Align tool's own derivation)
    const offs = wallLayerFaceOffsets(el.params.flipped, el.assembly)
    const k = Math.max(0, Math.min(offs.length - 1, lr.layer))
    off = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly) + offs[k]
  } else if (lr.face && el.category === 'wall' && el.assembly && el.assemblyThicknessFt > 0) {
    // stored path → centerline → reference face, along the path's LEFT normal —
    // the same derivation snap.ts uses for its layer-face candidates
    const t = el.assemblyThicknessFt
    const sign = el.params.flipped === true ? -1 : 1
    const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
    off = delta + sign * (t / 2 - referenceOffsets(el.assembly)[lr.face])
  }
  const n: Pt2 = [-u[1], u[0]]
  return { p: [a[0] + n[0] * off, a[1] + n[1] * off], u }
}

/**
 * DIMENSION EQ round: the CURRENT line an openingRef witness measures to — one of its
 * door/window's reference lines across the host wall. Null when the opening or its
 * host is gone (the witness degrades to its static point).
 */
export function openingWitnessLine(
  model: ModelDoc,
  or: NonNullable<DraftingWitness['openingRef']>,
): { p: Pt2; u: Pt2; rel: number } | null {
  const el = resolveElement(model, or.elementId)
  if (!el?.hostId) return null
  const ln = openingAlignLines(resolveElement(model, el.hostId), el)[or.line]
  if (!ln) return null
  const len = Math.hypot(ln.b[0] - ln.a[0], ln.b[1] - ln.a[1])
  if (len < 1e-9) return null
  return { p: ln.a, u: [(ln.b[0] - ln.a[0]) / len, (ln.b[1] - ln.a[1]) / len], rel: ln.rel }
}

/** ALIGNED ANY-LINE round: an element's plan footprint ring — freestanding objects,
 *  casework and columns, plus (OBJECT CENTERLINES round, 2026-09-30) objects hosted
 *  onto a surface (they keep their own anchor), ceiling lights (own anchor) and
 *  wall fixtures (their plate on the host face). Null for anything else. */
export function footprintRing(model: ModelDoc, elementId: ElementId): Pt2[] | null {
  const el = resolveElement(model, elementId)
  if (!el) return null
  const ownPath = !!(el.params.path as Path2D | undefined)?.nodes?.length
  if (el.category === 'column') return columnProfileRing(el)
  if (el.category === 'object' || el.category === 'casework') return ownPath ? objectFootprintCorners(el) : null
  if (el.category === 'fixture') {
    if (ownPath || isPointFixtureKind(el.typeParams?.fixtureKind as string | undefined)) return objectFootprintCorners(el)
    return el.hostId ? hostedFootprintCorners(resolveElement(model, el.hostId), el) : null
  }
  return null
}

/**
 * OBJECT CENTERLINES round (2026-09-30, Matt: "hidden centerline references to all
 * objects so we may use aligned dimensions set to their centers — both directions"):
 * every footprint ring offers, besides its edges, two HIDDEN reference lines through
 * the footprint center — axis 0 parallel to the ring's first edge (the width run),
 * axis 1 perpendicular to it. Never drawn; they exist only as aligned-dimension
 * references (highlighted while the Dimension tool hovers them). Returned as
 * segments spanning the footprint so the hover highlight reads at the right length.
 */
export function footprintCenterlines(ring: Pt2[]): [Pt2, Pt2][] {
  if (ring.length < 3) return []
  let cx = 0, cy = 0
  for (const p of ring) { cx += p[0]; cy += p[1] }
  cx /= ring.length; cy /= ring.length
  const e = lineOf(ring[0], ring[1])
  if (!e) return []
  const axes: Pt2[] = [e.u, [-e.u[1], e.u[0]]]
  return axes.map((u) => {
    let lo = 0, hi = 0
    for (const p of ring) {
      const t = (p[0] - cx) * u[0] + (p[1] - cy) * u[1]
      if (t < lo) lo = t
      if (t > hi) hi = t
    }
    return [[cx + u[0] * lo, cy + u[1] * lo], [cx + u[0] * hi, cy + u[1] * hi]] as [Pt2, Pt2]
  })
}

/** the segment a footRef witness measures to — a footprint edge, or (axis set) one
 *  of the two hidden centerlines */
export function footRefSegment(model: ModelDoc, fr: NonNullable<DraftingWitness['footRef']>): [Pt2, Pt2] | null {
  const ring = footprintRing(model, fr.elementId)
  if (!ring || ring.length < 2) return null
  if (fr.axis === 0 || fr.axis === 1) return footprintCenterlines(ring)[fr.axis] ?? null
  return [ring[fr.edge % ring.length], ring[(fr.edge + 1) % ring.length]]
}

const lineOf = (a: Pt2, b: Pt2): { p: Pt2; u: Pt2 } | null => {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1])
  return len < 1e-9 ? null : { p: [a[0], a[1]], u: [(b[0] - a[0]) / len, (b[1] - a[1]) / len] }
}

/** the live line a gridRef / footRef witness measures to */
function gridWitnessLine(model: ModelDoc, gridId: GridId): { p: Pt2; u: Pt2 } | null {
  const g = listGrids(model).find((x) => x.id === gridId)
  return g ? lineOf(g.a, g.b) : null
}
function footWitnessLine(model: ModelDoc, fr: NonNullable<DraftingWitness['footRef']>): { p: Pt2; u: Pt2 } | null {
  const seg = footRefSegment(model, fr)
  return seg ? lineOf(seg[0], seg[1]) : null
}

/** the live reference line behind a line-anchored witness (wall/edge, opening, grid,
 *  footprint edge), or null */
export function witnessRefLine(model: ModelDoc, w: DraftingWitness): { p: Pt2; u: Pt2 } | null {
  if (w.lineRef) return witnessLine(model, w.lineRef)
  if (w.openingRef) return openingWitnessLine(model, w.openingRef)
  if (w.gridRef) return gridWitnessLine(model, w.gridRef.gridId)
  if (w.footRef) return footWitnessLine(model, w.footRef)
  return null
}

/** the element a witness is anchored to (line, opening or node), or null for a bare point */
export function witnessElementId(w: DraftingWitness): ElementId | null {
  return w.lineRef?.elementId ?? w.openingRef?.elementId ?? w.footRef?.elementId ?? w.ref?.elementId ?? null
}

/** Two witnesses reference the same line (wall edge + face, or the same opening line). */
export function sameWitnessRef(a: DraftingWitness, b: DraftingWitness): boolean {
  if (a.lineRef && b.lineRef) return sameLineRef(a.lineRef, b.lineRef)
  if (a.openingRef && b.openingRef) {
    return a.openingRef.elementId === b.openingRef.elementId && a.openingRef.line === b.openingRef.line
  }
  if (a.gridRef && b.gridRef) return a.gridRef.gridId === b.gridRef.gridId
  if (a.draftRef && b.draftRef) return a.draftRef.recId === b.draftRef.recId && a.draftRef.nodeId === b.draftRef.nodeId
  if (a.footRef && b.footRef) {
    return a.footRef.elementId === b.footRef.elementId && a.footRef.edge === b.footRef.edge
      && (a.footRef.axis ?? null) === (b.footRef.axis ?? null)
  }
  return false
}

const projectOntoLine = (line: { p: Pt2; u: Pt2 }, at: Pt2): Pt2 => {
  const t = (at[0] - line.p[0]) * line.u[0] + (at[1] - line.p[1]) * line.u[1]
  return [line.p[0] + line.u[0] * t, line.p[1] + line.u[1] * t]
}

const distToSegment = (p: Pt2, a: Pt2, b: Pt2): number => {
  const abx = b[0] - a[0], aby = b[1] - a[1]
  const L2 = abx * abx + aby * aby
  const t = L2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / L2)) : 0
  return Math.hypot(p[0] - (a[0] + abx * t), p[1] - (a[1] + aby * t))
}

/**
 * REVIT ALIGN round: how far off exactly-parallel an edge may sit and still join an
 * aligned string (sin of the angle) — generous enough for float noise and hand-drawn
 * walls a hair off axis, far too tight for a genuinely skewed wall (0.5°).
 */
export const ALIGNED_PARALLEL_TOL_SIN = Math.sin((0.5 * Math.PI) / 180)

/** Two line anchors reference the same line — same element edge, same wall face. */
export function sameLineRef(
  a: NonNullable<DraftingWitness['lineRef']>,
  b: NonNullable<DraftingWitness['lineRef']>,
): boolean {
  return a.elementId === b.elementId && a.nodeId === b.nodeId && (a.face ?? null) === (b.face ?? null)
    && (a.layer ?? null) === (b.layer ?? null)
}

/**
 * DIMENSIONS SUITE (ALIGNED): resolve a pick-the-wall click into a witness. Finds
 * the picked element's nearest STRAIGHT edge to the click; for a wall with an
 * assembly the `refFace` choice resolves to a concrete reference line — 'core' /
 * 'finish' pick the exterior-or-interior face NEAREST THE CLICK (the clicked side
 * decides), 'center' the wall centerline. The witness anchors to that LINE
 * (lineRef) with the click's projection as its static point, so the value derives
 * from the wall's current geometry forever after. Non-wall elements (and walls with
 * no assembly) anchor to the raw edge itself.
 *
 * REVIT ALIGN round: `parallelTo` (a unit direction) restricts the candidate edges
 * to ones PARALLEL to it — the Revit rule that every reference in an aligned string
 * lies parallel to the first, so the string's projected values stay meaningful. Null
 * when the element has no qualifying edge (the caller says why).
 */
export function alignedPickWitness(
  model: ModelDoc,
  elementId: ElementId,
  at: Pt2,
  refFace: 'core' | 'finish' | 'center' | 'any' = 'core',
  parallelTo: Pt2 | null = null,
): DraftingWitness | null {
  const el = resolveElement(model, elementId)
  const parallelOk = (u: Pt2) => !parallelTo || Math.abs(u[0] * parallelTo[1] - u[1] * parallelTo[0]) <= ALIGNED_PARALLEL_TOL_SIN
  // ALIGNED ANY-LINE round: a freestanding element (object / casework / column)
  // offers every edge of its plan footprint — and (OBJECT CENTERLINES round) its two
  // hidden centerlines, so a dimension can land on an object's center either way
  const ring = el && el.category !== 'door' && el.category !== 'window' ? footprintRing(model, elementId) : null
  if (ring && ring.length >= 2) {
    let pickF: { ref: NonNullable<DraftingWitness['footRef']>; d: number; pt: Pt2 } | null = null
    const consider = (a: Pt2, b: Pt2, ref: NonNullable<DraftingWitness['footRef']>) => {
      const ln = lineOf(a, b)
      if (!ln || !parallelOk(ln.u)) return
      const d = distToSegment(at, a, b)
      if (!pickF || d < pickF.d) pickF = { ref, d, pt: projectOntoLine(ln, at) }
    }
    for (let i = 0; i < ring.length; i++) consider(ring[i], ring[(i + 1) % ring.length], { elementId, edge: i })
    footprintCenterlines(ring).forEach(([a, b], k) => consider(a, b, { elementId, edge: 0, axis: k as 0 | 1 }))
    const pf = pickF as { ref: NonNullable<DraftingWitness['footRef']>; d: number; pt: Pt2 } | null
    return pf ? { pt: pf.pt, footRef: pf.ref } : null
  }
  // DIMENSION EQ round: a hosted door/window offers its five reference lines (frame
  // edges, jamb faces, centerline — each across its wall), nearest to the click wins
  if (el?.hostId && (el.category === 'door' || el.category === 'window')) {
    const lines = openingAlignLines(resolveElement(model, el.hostId), el)
    let pickO: { i: number; d: number; pt: Pt2 } | null = null
    lines.forEach((ln, i) => {
      const lx = ln.b[0] - ln.a[0], ly = ln.b[1] - ln.a[1]
      const len = Math.hypot(lx, ly)
      if (len < 1e-9) return
      if (parallelTo && Math.abs((lx * parallelTo[1] - ly * parallelTo[0]) / len) > ALIGNED_PARALLEL_TOL_SIN) return
      const pt = projectOntoLine({ p: ln.a, u: [lx / len, ly / len] }, at)
      const d = Math.hypot(at[0] - pt[0], at[1] - pt[1])
      if (!pickO || d < pickO.d) pickO = { i, d, pt }
    })
    const po = pickO as { i: number; d: number; pt: Pt2 } | null
    return po ? { pt: po.pt, openingRef: { elementId, line: po.i } } : null
  }
  const path = (el?.params.path ?? el?.params.boundary) as Path2D | null | undefined
  if (!el || !path?.nodes || path.nodes.length < 2) return null
  let best: { i: number; d: number } | null = null
  const count = path.closed ? path.nodes.length : path.nodes.length - 1
  for (let i = 0; i < count; i++) {
    if (path.nodes[i].bulge) continue   // curved spans excluded (CURVES-round scope)
    const a = path.nodes[i].pt, b = path.nodes[(i + 1) % path.nodes.length].pt
    if (parallelTo) {
      const ex = b[0] - a[0], ey = b[1] - a[1]
      const len = Math.hypot(ex, ey)
      if (len < 1e-9) continue
      if (Math.abs((ex * parallelTo[1] - ey * parallelTo[0]) / len) > ALIGNED_PARALLEL_TOL_SIN) continue
    }
    const d = distToSegment(at, a, b)
    if (!best || d < best.d) best = { i, d }
  }
  if (!best) return null
  const nodeId = path.nodes[best.i].id
  if (refFace === 'any' && el.category === 'wall' && el.assembly && el.assemblyThicknessFt > 0) {
    // ALIGNED ANY-LINE round (Revit's individual references): every line of the
    // assembly — each layer boundary plus the centerline — nearest to the click wins
    const nL = wallLayerFaceOffsets(el.params.flipped, el.assembly).length
    let pick: { w: DraftingWitness; d: number } | null = null
    const tryRef = (lineRef: NonNullable<DraftingWitness['lineRef']>) => {
      const line = witnessLine(model, lineRef)
      if (!line) return
      const proj = projectOntoLine(line, at)
      const d = Math.hypot(at[0] - proj[0], at[1] - proj[1])
      if (!pick || d < pick.d) pick = { w: { pt: proj, lineRef }, d }
    }
    for (let k = 0; k < nL; k++) tryRef({ elementId, nodeId, layer: k })
    tryRef({ elementId, nodeId, face: 'wallCenterline' })
    const pk = pick as { w: DraftingWitness; d: number } | null
    if (pk) return pk.w
  }
  if (el.category === 'wall' && el.assembly && el.assemblyThicknessFt > 0) {
    const faces: WallReferenceLine[] =
      refFace === 'center' ? ['wallCenterline']
        : refFace === 'finish' ? ['finishFaceExterior', 'finishFaceInterior']
          : ['coreFaceExterior', 'coreFaceInterior']
    let pick: { face: WallReferenceLine; pt: Pt2; d: number } | null = null
    for (const face of faces) {
      const line = witnessLine(model, { elementId, nodeId, face })
      if (!line) continue
      const proj = projectOntoLine(line, at)
      const d = Math.hypot(at[0] - proj[0], at[1] - proj[1])
      if (!pick || d < pick.d) pick = { face, pt: proj, d }
    }
    if (pick) return { pt: pick.pt, lineRef: { elementId, nodeId, face: pick.face } }
  }
  const line = witnessLine(model, { elementId, nodeId })
  if (!line) return null
  return { pt: projectOntoLine(line, at), lineRef: { elementId, nodeId } }
}

/** ALIGNED ANY-LINE round: a witness on a grid line (null when not parallel) */
export function gridPickWitness(model: ModelDoc, gridId: GridId, at: Pt2, parallelTo: Pt2 | null = null): DraftingWitness | null {
  const line = gridWitnessLine(model, gridId)
  if (!line) return null
  if (parallelTo && Math.abs(line.u[0] * parallelTo[1] - line.u[1] * parallelTo[0]) > ALIGNED_PARALLEL_TOL_SIN) return null
  return { pt: projectOntoLine(line, at), gridRef: { gridId } }
}

/** ALIGNED ANY-LINE round: a STATIC witness on any other line (an imported DWG/DXF
 *  line, a drafted line) — measured where it is now, not associative */
export function staticLinePickWitness(a: Pt2, b: Pt2, at: Pt2, parallelTo: Pt2 | null = null): DraftingWitness | null {
  const line = lineOf(a, b)
  if (!line) return null
  if (parallelTo && Math.abs(line.u[0] * parallelTo[1] - line.u[1] * parallelTo[0]) > ALIGNED_PARALLEL_TOL_SIN) return null
  return { pt: projectOntoLine(line, at) }
}

/**
 * The CURRENT position a witness measures: element+node refs re-resolve against the
 * live path/boundary; a lineRef witness (ALIGNED) projects its stored point onto the
 * CURRENT reference line (values derive — move the wall and the clear distance
 * re-reads; slide it along itself and nothing changes). A missing element/node/edge
 * falls back to the last static point (recorded, not erased).
 */
export function resolveWitness(model: ModelDoc, w: DraftingWitness): Pt2 {
  if (w.draftRef) {
    // DRAFTING BASELINE: the referenced drafting record's node, live
    const rec = getDrafting(model, w.draftRef.recId)
    const node = rec?.path?.nodes?.find((n) => n.id === w.draftRef!.nodeId)
    if (node) return [node.pt[0], node.pt[1]]
    // DETAIL COMPONENT SNAPS (2026-10-13): a component's insertion point / a closed
    // piece's center follow the component live too
    if (rec && w.draftRef.nodeId === ('ins' as NodeId) && rec.componentPlace?.at) return [rec.componentPlace.at[0], rec.componentPlace.at[1]]
    if (rec?.path?.nodes?.length && w.draftRef.nodeId === ('ctr' as NodeId)) {
      const xs = rec.path.nodes.map((n) => n.pt[0]), ys = rec.path.nodes.map((n) => n.pt[1])
      return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2]
    }
    return [w.pt[0], w.pt[1]]
  }
  if (w.lineRef || w.openingRef || w.gridRef || w.footRef) {
    const line = witnessRefLine(model, w)
    if (line) {
      const t = (w.pt[0] - line.p[0]) * line.u[0] + (w.pt[1] - line.p[1]) * line.u[1]
      return [line.p[0] + line.u[0] * t, line.p[1] + line.u[1] * t]
    }
  }
  if (w.ref) {
    const el = resolveElement(model, w.ref.elementId)
    const path = (el?.params.path ?? el?.params.boundary) as Path2D | null | undefined
    const node = path?.nodes?.find((n) => n.id === w.ref!.nodeId)
    if (node) return [node.pt[0], node.pt[1]]
  }
  return [w.pt[0], w.pt[1]]
}

/**
 * The record's witness list in pick order — the r22 string form when present,
 * else the legacy a/b pair. Always ≥2 entries for a well-formed dimension.
 */
export function dimensionWitnessList(rec: DraftingRecord): DraftingWitness[] {
  if (rec.kind !== 'dimension') return []
  if (rec.witnesses && rec.witnesses.length >= 2) return rec.witnesses
  return rec.a && rec.b ? [rec.a, rec.b] : []
}

/** Every measured point of a dimension, resolved live (values derive). */
export function dimensionPoints(model: ModelDoc, rec: DraftingRecord): Pt2[] {
  return dimensionWitnessList(rec).map((w) => resolveWitness(model, w))
}

/** Resolved OUTER endpoints of a dimension record — the derived overall measurement. */
export function dimensionEnds(model: ModelDoc, rec: DraftingRecord): [Pt2, Pt2] | null {
  const pts = dimensionPoints(model, rec)
  if (pts.length < 2) return null
  return [pts[0], pts[pts.length - 1]]
}

export function dimensionValueFt(model: ModelDoc, rec: DraftingRecord): number | null {
  // CURVES round: radius / arc-length values derive from the LIVE arc
  if (rec.dimType === 'radius' || rec.dimType === 'arclen') {
    const arc = dimensionEdgeArc(model, rec)
    if (arc) return rec.dimType === 'radius' ? arc.r : arc.r * Math.abs(arc.sweep)
    // degraded: radius falls back to |center − arc point| of the stored witnesses;
    // arc length to the chord (both from the static a/b pair captured at creation)
    const ends = dimensionEnds(model, rec)
    if (!ends) return null
    return Math.hypot(ends[1][0] - ends[0][0], ends[1][1] - ends[0][1])
  }
  const ends = dimensionEnds(model, rec)
  if (!ends) return null
  // DIMENSIONS SUITE: a direction-locked string measures PROJECTIONS along it
  // (cartesian axis → Δx/Δy), matching what the rendered crossings read
  const dir = dimensionDirection(model, rec)
  if (dir) return Math.abs((ends[1][0] - ends[0][0]) * dir[0] + (ends[1][1] - ends[0][1]) * dir[1])
  return Math.hypot(ends[1][0] - ends[0][0], ends[1][1] - ends[0][1])
}

/** A text record's leader arrow points — r22 `leaders` preferred, legacy `leader` wrapped. */
export function textLeaderPoints(rec: DraftingRecord): Pt2[] {
  if (rec.kind !== 'text') return []
  if (rec.leaders && rec.leaders.length) return rec.leaders
  return rec.leader ? [rec.leader] : []
}

export { getDrafting, listDrafting }
